import qs from 'qs';
import type {
  AiFootageRequest,
  BankFootageQuery,
  BankFootageResponse,
  BankFootageSuggestResponse,
  CompleteMultipartUploadRequest,
  CreateMusicVideoCutRequest,
  FootageClipResponse,
  FootageListResponse,
  FootageUploadStartRequest,
  MakeMusicVideoResponse,
  MusicVideoCutResponse,
  MusicVideoEditorResponse,
  PresignedMultipartUploadResponse,
  RecutMusicVideoRequest,
  UpdateMusicVideoCutRequest,
} from 'fym-shared/src/music24/musicVideoApi';
import {
  MUSIC_VIDEO_SHOWCASE_PATHS,
  type MusicVideoShowcaseBankQuery,
  type MusicVideoShowcaseBankResponse,
  type MusicVideoShowcaseResponse,
} from 'fym-shared/src/music24/musicVideoShowcase';
import { fymAPI, VIDEO_CREDIT_TAGS } from '../../redux/api/fymAPI';
import type { SerializedKitAsset } from '../../redux/api/fymAPI.types';

/**
 * The routes of the music video editor (spec `2026-10-09-music24-music-video-editor-design.md`
 * §4.3 footage, §4.4 cuts). The shapes are the shared contract `fym-shared/src/music24/musicVideoApi.ts`.
 * The endpoints go into `fymAPI` with their own tag types, so the realtime events
 * (`realtimeEventTags.ts`) and the other screens use the same cache.
 *
 * The public routes of the Studio showcase (`/public/music-video-showcase`) are here too: the
 * showcase mode of the editor reads them in place of the account routes.
 *
 * Tags: `MusicVideoEditor` (id = the catalog item uuid: the song, the cut list, the plan and the
 * credits of the editor), `MusicVideoCut` (id = the cut uuid: one document), `Footage` (`LIST` = the
 * clips of the account, `BANK` = the bank lists).
 */

/** The tag id of the footage list of the account. */
export const FOOTAGE_LIST_ID = 'LIST';
/** The tag id of the bank lists (search and the suggestions). */
export const FOOTAGE_BANK_ID = 'BANK';
/** The id of the release kit list tag (`RELEASE_KIT_LIST_ID` in `fymAPI.ts`). */
const RELEASE_KIT_LIST_ID = 'LIST';

interface SongArgs {
  accountId: number;
  catalogItemUuid: string;
}

interface CutArgs extends SongArgs {
  cutUuid: string;
}

export type GetMusicVideoEditorOptions = SongArgs;
export type CreateMusicVideoCutOptions = SongArgs & CreateMusicVideoCutRequest;
export type GetMusicVideoCutOptions = CutArgs;
export type UpdateMusicVideoCutOptions = CutArgs & UpdateMusicVideoCutRequest;
export type DeleteMusicVideoCutOptions = CutArgs;
export type RecutMusicVideoCutOptions = CutArgs & RecutMusicVideoRequest;
export type MakeMusicVideoOptions = CutArgs;
export type GetFootageBankOptions = { accountId: number } & BankFootageQuery;
export type GetFootageBankSuggestOptions = SongArgs & { section: number };
export type GetFootageOptions = { accountId: number; catalogItemUuid?: string };
export type StartFootageUploadOptions = { accountId: number } & FootageUploadStartRequest;
export type CompleteFootageUploadOptions = { accountId: number; uploadId: string } & CompleteMultipartUploadRequest;
export type AbortFootageUploadOptions = { accountId: number; uploadId: string; key: string };
export type ArchiveFootageOptions = { accountId: number; clipUuid: string };
export type CreateAiFootageOptions = { accountId: number } & AiFootageRequest;

function editorUrl({ accountId, catalogItemUuid }: SongArgs): string {
  return `accounts/${accountId}/music-video/${encodeURIComponent(catalogItemUuid)}`;
}

function cutUrl(args: CutArgs): string {
  return `${editorUrl(args)}/cuts/${encodeURIComponent(args.cutUuid)}`;
}

const JSON_HEADERS = { 'content-type': 'application/json' };

/** A showcase path below the API base (the base ends with a slash). */
function showcasePath(path: string): string {
  return path.replace(/^\//, '');
}

/** The showcase answer is a frozen snapshot: keep it for the whole visit. */
const SHOWCASE_CACHE_SECONDS = 60 * 60;

export const musicVideoEditorApi = fymAPI
  .enhanceEndpoints({ addTagTypes: ['MusicVideoEditor', 'MusicVideoCut', 'Footage'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      // ------------------------------------------ §4.4 cuts ------------------------------------------
      getMusicVideoEditor: builder.query<MusicVideoEditorResponse, GetMusicVideoEditorOptions>({
        query: (args) => ({ url: editorUrl(args), method: 'GET' }),
        providesTags: (response, error, { catalogItemUuid }) => [
          { type: 'MusicVideoEditor' as const, id: catalogItemUuid },
        ],
      }),
      // No body: the next cut, and the director makes it. `fromCutUuid`: a copy of that cut.
      createMusicVideoCut: builder.mutation<MusicVideoCutResponse, CreateMusicVideoCutOptions>({
        query: ({ fromCutUuid, ...args }) => ({
          url: `${editorUrl(args)}/cuts`,
          method: 'POST',
          body: JSON.stringify(fromCutUuid === undefined ? {} : { fromCutUuid }),
          headers: JSON_HEADERS,
        }),
        invalidatesTags: (response, error, { catalogItemUuid }) => [
          { type: 'MusicVideoEditor' as const, id: catalogItemUuid },
        ],
      }),
      getMusicVideoCut: builder.query<MusicVideoCutResponse, GetMusicVideoCutOptions>({
        query: (args) => ({ url: cutUrl(args), method: 'GET' }),
        providesTags: (response, error, { cutUuid }) => [{ type: 'MusicVideoCut' as const, id: cutUuid }],
      }),
      // The autosave. It invalidates nothing: the editor holds the document and the revision of
      // the answer (`useCutAutosave`). The realtime event of the save reads the cut again; that
      // read has the same revision, so the editor keeps what the user types.
      updateMusicVideoCut: builder.mutation<MusicVideoCutResponse, UpdateMusicVideoCutOptions>({
        query: ({ revision, doc, ...args }) => ({
          url: cutUrl(args),
          method: 'PATCH',
          body: JSON.stringify({ revision, doc }),
          headers: JSON_HEADERS,
        }),
      }),
      deleteMusicVideoCut: builder.mutation<void, DeleteMusicVideoCutOptions>({
        query: (args) => ({ url: cutUrl(args), method: 'DELETE' }),
        invalidatesTags: (response, error, { catalogItemUuid }) => [
          { type: 'MusicVideoEditor' as const, id: catalogItemUuid },
        ],
      }),
      // 202: the director works. The cut list shows its state; the cut reads again at the event.
      recutMusicVideoCut: builder.mutation<MusicVideoCutResponse, RecutMusicVideoCutOptions>({
        query: ({ sections, chips, text, ...args }) => ({
          url: `${cutUrl(args)}/recut`,
          method: 'POST',
          body: JSON.stringify(text === undefined ? { sections, chips } : { sections, chips, text }),
          headers: JSON_HEADERS,
        }),
        invalidatesTags: (response, error, { catalogItemUuid, cutUuid }) => [
          { type: 'MusicVideoEditor' as const, id: catalogItemUuid },
          { type: 'MusicVideoCut' as const, id: cutUuid },
        ],
      }),
      // "Make the video" (3 credits). Its 402 and 403 show the editor's own states
      // (`ENDPOINTS_WITH_OWN_LIMIT_UI`, frames e4, e5). A refusal reads the credits too.
      makeMusicVideo: builder.mutation<MakeMusicVideoResponse<SerializedKitAsset>, MakeMusicVideoOptions>({
        query: (args) => ({ url: `${cutUrl(args)}/make`, method: 'POST', body: '{}', headers: JSON_HEADERS }),
        invalidatesTags: (response, error, { catalogItemUuid }) => [
          { type: 'MusicVideoEditor' as const, id: catalogItemUuid },
          { type: 'ReleaseKit' as const, id: catalogItemUuid },
          { type: 'ReleaseKit' as const, id: RELEASE_KIT_LIST_ID },
          ...VIDEO_CREDIT_TAGS,
          'Work' as const,
          'Releases' as const,
        ],
      }),

      // ------------------------------------------ §4.3 footage ------------------------------------------
      getFootageBank: builder.query<BankFootageResponse, GetFootageBankOptions>({
        // `uuids` goes as one comma list (`uuids=a,b`), the form the route reads.
        query: ({ accountId, uuids, ...query }) => ({
          url: `accounts/${accountId}/footage/bank?${qs.stringify(
            { ...query, uuids: uuids === undefined ? undefined : uuids.join(',') },
            { skipNulls: true }
          )}`,
          method: 'GET',
        }),
        providesTags: [{ type: 'Footage' as const, id: FOOTAGE_BANK_ID }],
      }),
      getFootageBankSuggest: builder.query<BankFootageSuggestResponse, GetFootageBankSuggestOptions>({
        query: ({ accountId, catalogItemUuid, section }) => ({
          url: `accounts/${accountId}/footage/bank/suggest?${qs.stringify({ catalogItemUuid, section })}`,
          method: 'GET',
        }),
        providesTags: [{ type: 'Footage' as const, id: FOOTAGE_BANK_ID }],
      }),
      getFootage: builder.query<FootageListResponse, GetFootageOptions>({
        query: ({ accountId, catalogItemUuid }) => ({
          url: `accounts/${accountId}/footage${catalogItemUuid === undefined ? '' : `?${qs.stringify({ catalogItemUuid })}`}`,
          method: 'GET',
        }),
        providesTags: [{ type: 'Footage' as const, id: FOOTAGE_LIST_ID }],
      }),
      startFootageUpload: builder.mutation<PresignedMultipartUploadResponse, StartFootageUploadOptions>({
        query: ({ accountId, fileName, contentType, sizeBytes, catalogItemUuid }) => ({
          url: `accounts/${accountId}/footage/uploads`,
          method: 'POST',
          body: JSON.stringify({ fileName, contentType, sizeBytes, catalogItemUuid }),
          headers: JSON_HEADERS,
        }),
      }),
      completeFootageUpload: builder.mutation<FootageClipResponse, CompleteFootageUploadOptions>({
        query: ({ accountId, uploadId, key, parts }) => ({
          url: `accounts/${accountId}/footage/uploads/${encodeURIComponent(uploadId)}/complete`,
          method: 'POST',
          body: JSON.stringify({ key, parts }),
          headers: JSON_HEADERS,
        }),
        invalidatesTags: [{ type: 'Footage' as const, id: FOOTAGE_LIST_ID }],
      }),
      abortFootageUpload: builder.mutation<void, AbortFootageUploadOptions>({
        query: ({ accountId, uploadId, key }) => ({
          url: `accounts/${accountId}/footage/uploads/${encodeURIComponent(uploadId)}?${qs.stringify({ key })}`,
          method: 'DELETE',
        }),
      }),
      archiveFootage: builder.mutation<void, ArchiveFootageOptions>({
        query: ({ accountId, clipUuid }) => ({
          url: `accounts/${accountId}/footage/${encodeURIComponent(clipUuid)}`,
          method: 'DELETE',
        }),
        invalidatesTags: [{ type: 'Footage' as const, id: FOOTAGE_LIST_ID }],
      }),
      // One AI clip (4 credits). Its 402 and 403 show the AI tab's own states
      // (`ENDPOINTS_WITH_OWN_LIMIT_UI`, frame h4). A refusal reads the credits too.
      createAiFootage: builder.mutation<FootageClipResponse, CreateAiFootageOptions>({
        query: ({ accountId, catalogItemUuid, prompt, style, people, referenceFileIds, styleText }) => ({
          url: `accounts/${accountId}/footage/ai`,
          method: 'POST',
          body: JSON.stringify({ catalogItemUuid, prompt, style, people, referenceFileIds, styleText }),
          headers: JSON_HEADERS,
        }),
        invalidatesTags: (response, error, { catalogItemUuid }) => [
          { type: 'Footage' as const, id: FOOTAGE_LIST_ID },
          { type: 'MusicVideoEditor' as const, id: catalogItemUuid },
          ...VIDEO_CREDIT_TAGS,
        ],
      }),

      // ------------------------------- the Studio showcase (public, no session) -------------------------------
      // The demo on music24.com (spec 2026-10-10 Studio showcase §3.5): the frozen "Forests" cut, the
      // published bank search (429 `M24_RATE_LIMITED` over 30 a minute) and the lookup of the clips of
      // the cut. No tag: no account route changes these answers.
      getMusicVideoShowcase: builder.query<MusicVideoShowcaseResponse, void>({
        query: () => ({ url: showcasePath(MUSIC_VIDEO_SHOWCASE_PATHS.showcase), method: 'GET' }),
        keepUnusedDataFor: SHOWCASE_CACHE_SECONDS,
      }),
      getMusicVideoShowcaseBank: builder.query<MusicVideoShowcaseBankResponse, MusicVideoShowcaseBankQuery>({
        query: (query) => ({
          url: `${showcasePath(MUSIC_VIDEO_SHOWCASE_PATHS.bank)}?${qs.stringify(query, { skipNulls: true })}`,
          method: 'GET',
        }),
      }),
      getMusicVideoShowcaseLookup: builder.query<MusicVideoShowcaseBankResponse, { uuids: string[] }>({
        query: ({ uuids }) => ({
          url: `${showcasePath(MUSIC_VIDEO_SHOWCASE_PATHS.bankLookup)}?${qs.stringify({ uuids: uuids.join(',') })}`,
          method: 'GET',
        }),
      }),
    }),
  });

export const {
  useGetMusicVideoEditorQuery,
  useCreateMusicVideoCutMutation,
  useGetMusicVideoCutQuery,
  useUpdateMusicVideoCutMutation,
  useDeleteMusicVideoCutMutation,
  useRecutMusicVideoCutMutation,
  useMakeMusicVideoMutation,
  useGetFootageBankQuery,
  useLazyGetFootageBankQuery,
  useGetFootageBankSuggestQuery,
  useGetFootageQuery,
  useStartFootageUploadMutation,
  useCompleteFootageUploadMutation,
  useAbortFootageUploadMutation,
  useArchiveFootageMutation,
  useCreateAiFootageMutation,
  useGetMusicVideoShowcaseQuery,
  useGetMusicVideoShowcaseBankQuery,
  useLazyGetMusicVideoShowcaseLookupQuery,
} = musicVideoEditorApi;
